import { useState, useEffect, useRef } from 'react';
import { raioXRepository } from '../../data/raioXRepository';
import type { MusicaRaioX } from '@/shared/types';

/** A faixa entra só depois que o NowPlayingToast (8s) sai. */
const ATRASO_MS = 10_000;
/** Três cartões de 6s. */
const DURACAO_MS = 18_000;
/** Música mais curta que isso acabaria antes da faixa terminar. */
const DURACAO_MINIMA_SEG = 40;

export interface UseRaioXMusicaParams {
  /** modoPlaylistJukebox && playerConfig.exibir_raiox_musica */
  habilitado:      boolean;
  videoId:         string | null;
  titulo:          string | null;
  canal:           string | null;
  /** Duração conhecida pelo Player. `null` não bloqueia. */
  duracaoSegundos: number | null;
  /** true enquanto o PedidoPopup está no ar — a faixa não entra por cima. */
  bloqueado:       boolean;
}

export interface UseRaioXMusicaReturn {
  dados:   MusicaRaioX | null;
  visivel: boolean;
}

/**
 * Busca o raio-x ao trocar de faixa e controla a janela de exibição:
 * entra aos 10s, fica 18s, não volta mais naquela música.
 *
 * Os timers são longos e a TV roda 24/7, então o estado de bloqueio é lido por
 * ref (padrão `cbRef` do projeto) em vez da closure capturada.
 */
export function useRaioXMusica({
  habilitado, videoId, titulo, canal, duracaoSegundos, bloqueado,
}: UseRaioXMusicaParams): UseRaioXMusicaReturn {
  const [dados,   setDados]   = useState<MusicaRaioX | null>(null);
  const [visivel, setVisivel] = useState(false);

  const bloqueadoRef = useRef(bloqueado);
  useEffect(() => { bloqueadoRef.current = bloqueado; });

  /** true = a janela deveria ter aberto mas o PedidoPopup estava no ar. */
  const pendenteRef = useRef(false);
  const abrirTimerRef  = useRef<ReturnType<typeof setTimeout> | null>(null);
  const fecharTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const abrirRef = useRef<() => void>(() => {});

  useEffect(() => {
    abrirRef.current = () => {
      if (bloqueadoRef.current) { pendenteRef.current = true; return; }
      pendenteRef.current = false;
      setVisivel(true);
      fecharTimerRef.current = setTimeout(() => setVisivel(false), DURACAO_MS);
    };
  });

  // Troca de faixa: zera tudo, busca e agenda a entrada.
  useEffect(() => {
    if (abrirTimerRef.current)  clearTimeout(abrirTimerRef.current);
    if (fecharTimerRef.current) clearTimeout(fecharTimerRef.current);
    pendenteRef.current = false;
    setVisivel(false);
    setDados(null);

    if (!habilitado || !videoId || !titulo) return;
    if (duracaoSegundos !== null && duracaoSegundos < DURACAO_MINIMA_SEG) return;

    let cancelado = false;

    raioXRepository.obterRaioX(videoId, titulo, canal).then(r => {
      if (cancelado || !r?.encontrado) return;
      setDados(r);
      abrirTimerRef.current = setTimeout(() => abrirRef.current(), ATRASO_MS);
    });

    return () => {
      cancelado = true;
      if (abrirTimerRef.current)  clearTimeout(abrirTimerRef.current);
      if (fecharTimerRef.current) clearTimeout(fecharTimerRef.current);
    };
  }, [habilitado, videoId, titulo, canal, duracaoSegundos]);

  // Pedido do cliente saiu da tela e a faixa estava esperando a vez.
  useEffect(() => {
    if (!bloqueado && pendenteRef.current) abrirRef.current();
  }, [bloqueado]);

  return { dados, visivel };
}
